import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchDataVersions, createDataVersion, deleteDataVersion, updateDataVersion } from '../../common/jsonstore';
import { DataVersion } from '../../common/model';
import { AppState } from '../../redux/store/rootReducer';
import { AppDispatch } from '../../redux/store/store';
import { isTokenExpired } from '../../services/authenticationService';
import { signOut } from '../../services/localStorage';
import { handleError } from '../../services/messageApiService';

export interface IAuthentication {
  DataVersion: DataVersion[];
  success: any;
  isProcessingComplete: boolean;
}

const initialState: IAuthentication = { DataVersion: [], success: {}, isProcessingComplete: false };

const DataVersionSlice = createSlice({
  name: 'DataVersions',
  initialState,
  reducers: {
    receiveDataVersion: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        DataVersion: action.payload,
        isProcessingComplete: false
      };
    },
    success: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        success: action.payload,
        isProcessingComplete: true
      };
    }
  }
});


export const loadDataVersions = (): any => async (dispatch: AppDispatch) => {
  if (isTokenExpired()) {
    const DataVersions = await fetchDataVersions().then((res: any) => {
      dispatch(DataVersionSlice.actions.receiveDataVersion(res))
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data version not loaded';
        handleError(msg);
      }
    });
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const addDataVersion = (newDataVersion: DataVersion): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    const res = await createDataVersion(newDataVersion).then((resp: any) => {
      dispatch(DataVersionSlice.actions.receiveDataVersion(resp.data));
      dispatch(DataVersionSlice.actions.success({ 'sts': true, data: 'Data Version Added Successfully' }));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data Version Not Added';
        dispatch(DataVersionSlice.actions.success({ 'sts': false, data: msg }));
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const modifyDataVersion = (newDataVersion: DataVersion, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    const res = await createDataVersion(newDataVersion).then((resp: any) => {
      dispatch(DataVersionSlice.actions.receiveDataVersion(resp.data));
      dispatch(DataVersionSlice.actions.success({ 'sts': true, data: resp?.msg }));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data Version Not Updated';
        dispatch(DataVersionSlice.actions.success({ 'sts': false, data: msg }));
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const removeDataVersion = (id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (isTokenExpired()) {
    deleteDataVersion(id).then((res: any) => {
      const updatedDataVersions = getState().DataVersions.DataVersion.filter((item) => item.id !== id);
      dispatch(DataVersionSlice.actions.receiveDataVersion(updatedDataVersions));
      dispatch(DataVersionSlice.actions.success({ 'sts': true, data: 'Data Version deleted successfully' }));
    }).catch(err => {
      if (err?.response?.data?.message?.includes("access token")) {
        handleError(err.response.data.message);
        setTimeout(() => { return signOut(); }, 500);
      } else {
        let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data Version not deleted';
        dispatch(DataVersionSlice.actions.success({ 'sts': false, data: msg }));
      }
    })
  } else {
    handleError('Token is expired');
    setTimeout(() => { return signOut(); }, 500);
  }
}

export const updateSts = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
  dispatch(DataVersionSlice.actions.success(data));
}

export default DataVersionSlice.reducer;
